import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { OutlookMeetingResult } from '@cloudsign/shared';
import type { z } from 'zod';
import { t } from '../../lib/i18n';
import { formatDateTime } from '../../lib/format';
import { Alert, Button, CheckboxField, SelectField, Spinner } from '../../components/ui';
import { MeetingReadError, connectDialog, itemKeyOf, loadOffice, loadToken, readMeeting, saveToken } from './office';
import type { MeetingInfo, OfficeApi } from './office';
import { OutlookApiError, cancelMeeting, getOutlookContext, importMeeting } from './outlookApi';

type Result = z.output<typeof OutlookMeetingResult>;

const SKIP_KEYS: Record<string, string> = { 'outlook.skip.internal': 'w30.pane.skip.internal' };
const skipText = (key: string): string => t(SKIP_KEYS[key] ?? 'w30.pane.skip.other');

function problemText(e: unknown): string {
  if (e instanceof OutlookApiError) return t(e.messageKey);
  return t('error.internal');
}

/** The Outlook add-in task pane (ADR 0033): pre-register the open meeting's external guests with the organiser as host. */
export function OutlookTaskPane() {
  const [office, setOffice] = useState<OfficeApi | null>(null);
  const [officeState, setOfficeState] = useState<'loading' | 'ready' | 'missing'>('loading');
  const [token, setToken] = useState<string | null>(null);
  const [meeting, setMeeting] = useState<MeetingInfo | null>(null);
  const [meetingError, setMeetingError] = useState<string | null>(null);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [siteId, setSiteId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [result, setResult] = useState<Result | null>(null);

  useEffect(() => {
    let live = true;
    void loadOffice().then(async (o) => {
      if (!live) return;
      if (!o) return setOfficeState('missing');
      setOffice(o);
      setOfficeState('ready');
      setToken(loadToken(o));
      try {
        const m = await readMeeting(o);
        if (live) setMeeting(m);
      } catch (e) {
        if (!live) return;
        const reason = e instanceof MeetingReadError ? e.reason : 'failed';
        setMeetingError(reason === 'no_item' ? t('w30.outlook.noMeeting') : reason === 'unsaved' ? t('w30.outlook.saveFirst') : t('w30.outlook.readError'));
      }
    });
    return () => {
      live = false;
    };
  }, []);

  const ctx = useQuery({
    queryKey: ['outlook', 'context', token],
    enabled: token !== null,
    retry: false,
    queryFn: () => getOutlookContext(token as string),
  });
  const expired = ctx.error instanceof OutlookApiError && ctx.error.status === 401;
  useEffect(() => {
    if (expired) {
      saveToken(office, null);
      setToken(null);
      setConnectError(t('w30.outlook.sessionExpired'));
    }
  }, [expired, office]);

  const sites = ctx.data?.sites ?? [];
  const groups = (ctx.data?.groups ?? []).filter((g) => siteId === '' || g.siteIds.length === 0 || g.siteIds.includes(siteId));
  useEffect(() => {
    if (!ctx.data) return;
    if (siteId === '' && ctx.data.sites.length === 1) setSiteId(ctx.data.sites[0]!.id);
  }, [ctx.data, siteId]);
  useEffect(() => {
    if (groupId === '' && groups.length === 1) setGroupId(groups[0]!.id);
    else if (groupId !== '' && !groups.some((g) => g.id === groupId)) setGroupId('');
  }, [groups, groupId]);

  const run = useMutation({
    mutationFn: async (action: 'import' | 'cancel') => {
      if (!meeting || !token) throw new OutlookApiError(401, 'error.unauthenticated');
      const itemKey = await itemKeyOf(meeting.itemId);
      if (action === 'cancel') return cancelMeeting(token, { itemKey });
      return importMeeting(token, {
        itemKey,
        subject: meeting.subject.slice(0, 255),
        start: meeting.start,
        end: meeting.end,
        siteId,
        groupId,
        hostMemberId: ctx.data?.hostMemberId ?? null,
        attendees: meeting.attendees.slice(0, 50),
        sendEmail,
      });
    },
    onMutate: () => setResult(null),
    onSuccess: setResult,
    onError: (e) => {
      if (e instanceof OutlookApiError && e.status === 401) {
        saveToken(office, null);
        setToken(null);
        setConnectError(t('w30.outlook.sessionExpired'));
      }
    },
  });

  const connect = async () => {
    if (!office) return;
    setConnecting(true);
    setConnectError(null);
    try {
      const tok = await connectDialog(office, `${window.location.origin}/outlook/connect`);
      saveToken(office, tok);
      setToken(tok);
    } catch (e) {
      setConnectError(e instanceof Error && e.message === 'closed' ? t('w30.outlook.connectCancelled') : t('w30.outlook.connectFailed'));
    } finally {
      setConnecting(false);
    }
  };

  const when = useMemo(() => (meeting ? `${formatDateTime(meeting.start)} - ${formatDateTime(meeting.end)}` : ''), [meeting]);
  const canRun = meeting !== null && siteId !== '' && groupId !== '' && !run.isPending;

  return (
    <main className="mx-auto max-w-md space-y-4 p-3">
      <header>
        <h1 className="text-lg font-bold text-slate-900">{t('w30.outlook.title')}</h1>
        <p className="mt-1 text-sm text-slate-600">{t('w30.outlook.intro')}</p>
      </header>

      {officeState === 'loading' ? <Spinner /> : null}
      {officeState === 'missing' ? <Alert kind="info">{t('w30.outlook.notOutlook')}</Alert> : null}
      {meetingError ? <Alert>{meetingError}</Alert> : null}

      {officeState === 'ready' && token === null ? (
        <div className="space-y-3">
          <p className="text-sm">{t('w30.outlook.notConnected')}</p>
          {connectError ? <Alert>{connectError}</Alert> : null}
          <Button variant="primary" disabled={connecting} onClick={() => void connect()}>
            {connecting ? t('w30.outlook.connecting') : t('w30.outlook.connect')}
          </Button>
        </div>
      ) : null}

      {token !== null && ctx.isPending ? <Spinner /> : null}
      {token !== null && ctx.error && !expired ? <Alert>{problemText(ctx.error)}</Alert> : null}

      {token !== null && ctx.data ? (
        <div className="space-y-4">
          {meeting ? (
            <section aria-labelledby="w30-meeting" className="rounded-lg border border-slate-200 bg-white p-3 text-sm">
              <h2 id="w30-meeting" className="font-semibold">
                {t('w30.pane.meeting')}
              </h2>
              <dl className="mt-2 space-y-1">
                <div>
                  <dt className="inline text-slate-600">{t('w30.pane.subject')}: </dt>
                  <dd className="inline">{meeting.subject || t('w30.pane.noSubject')}</dd>
                </div>
                <div>
                  <dt className="inline text-slate-600">{t('w30.pane.when')}: </dt>
                  <dd className="inline">{when}</dd>
                </div>
              </dl>
            </section>
          ) : null}

          {sites.length === 0 ? (
            <Alert kind="info">{t('w30.pane.noSites')}</Alert>
          ) : (
            <>
              <SelectField label={t('w30.pane.site')} value={siteId} onChange={(e) => setSiteId(e.target.value)}>
                <option value="">{t('w30.pane.choose')}</option>
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </SelectField>
              <SelectField label={t('w30.pane.group')} value={groupId} onChange={(e) => setGroupId(e.target.value)}>
                <option value="">{t('w30.pane.choose')}</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </SelectField>
            </>
          )}

          {ctx.data.hostMemberId === null ? <Alert kind="info">{t('w30.pane.noHost')}</Alert> : null}

          {meeting ? (
            <section aria-labelledby="w30-guests" className="text-sm">
              <h2 id="w30-guests" className="font-semibold">
                {t('w30.pane.guests')}
              </h2>
              {meeting.attendees.length === 0 ? (
                <p className="mt-1 text-slate-600">{t('w30.pane.guestsNone')}</p>
              ) : (
                <>
                  <ul className="mt-1 divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
                    {meeting.attendees.map((a) => (
                      <li key={a.email} className="px-3 py-2">
                        {a.name ? <div className="font-medium">{a.name}</div> : null}
                        <div className="break-all text-slate-600">{a.email}</div>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-1 text-xs text-slate-600">{t('w30.pane.guestsHint')}</p>
                </>
              )}
            </section>
          ) : null}

          <CheckboxField label={t('w30.pane.sendEmail')} checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />

          {run.error ? <Alert>{problemText(run.error)}</Alert> : null}
          {run.isPending ? <p role="status" className="text-sm text-slate-600">{t('w30.pane.working')}</p> : null}
          {result ? (
            <div role="status" className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-900">
              <ul>
                <li>{t('w30.pane.resultCreated', { count: result.created })}</li>
                <li>{t('w30.pane.resultUpdated', { count: result.updated })}</li>
                <li>{t('w30.pane.resultCancelled', { count: result.cancelled })}</li>
              </ul>
              {result.skipped.length > 0 ? (
                <div className="mt-2">
                  <p className="font-medium">{t('w30.pane.resultSkipped')}</p>
                  <ul>
                    {result.skipped.map((s) => (
                      <li key={s.email} className="break-all">
                        {s.email} ({skipText(s.reasonKey)})
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <Button variant="primary" disabled={!canRun} onClick={() => run.mutate('import')}>
              {t('w30.pane.preRegister')}
            </Button>
            <Button disabled={meeting === null || run.isPending} onClick={() => run.mutate('cancel')}>
              {t('w30.pane.cancel')}
            </Button>
          </div>

          <div>
            <Button
              variant="ghost"
              onClick={() => {
                saveToken(office, null);
                setToken(null);
                setResult(null);
              }}
            >
              {t('w30.outlook.disconnect')}
            </Button>
          </div>
        </div>
      ) : null}
    </main>
  );
}
